import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { graphql, Script } from 'gatsby';
import LazyLoad from 'react-lazyload';
import styles from '@utils/styles';
import querystring from 'query-string';

import BazaarVoiceContainer from '@repo/ui-components/bazaarvoice/BazaarVoiceContainer';
import BlockBuilder from '@repo/ui-components/sanity/BlockBuilder';
import BreadcrumbWithWrapper from '@repo/ui-components/BreadcrumbWithWrapper';
import Button from '@repo/ui-components/button/Button';
import ContentArea from '@repo/ui-components/product-container/ContentArea';
import PageWrapper from '@repo/ui-components/page-wrapper/PageWrapper';
import PromoCallout from '@repo/ui-components/promo-callout/PromoCallout';
import ProductAttributes from '@repo/ui-components/ProductAttributes';
import ProductContainer from '@repo/ui-components/product-container/ProductContainer';
import ProductCard from '@repo/ui-components/product-card/ProductCard';
import ProductCardGrid from '@repo/ui-components/ProductCardGrid';
import Reviews from '@repo/ui-components/bazaarvoice/Reviews';
import QandA from '@repo/ui-components/bazaarvoice/QandA';
import VideoGallery from '@repo/ui-components/sanity/types/VideoGallery';

import getProductSchema from '@repo/utils/getProductSchema';
import createOpenGraphTags from '@repo/utils/createOpenGraphTags';

import Layout from '@components/layout';
import getInternalLink from '@components/utils/getInternalLink';
import IconShipping from '@components/svgs/shipping.svg';
import IconWarranty from '@components/svgs/warranty.svg';
import IconReturns from '@components/svgs/returns.svg';
import GearTagLine from '@components/GearTagLine';
import { getResizedProductImage } from '@repo/utils';
import { useSportdogInternationalContext } from '@util/static-translations';
import { showBazaarVoice } from '@util/page-helpers/showBazaarVoice';

const Callouts = styled.div`
  > div {
    padding-bottom: 0.5rem;
    padding-top: 0.5rem;
  }
`;

const FREE_SHIPPING_DISABLED_FOR = ['en-au'];

function ProductDetailTemplate({
  data,
  location: { state, search, pathname },
  pageContext,
}) {
  const { linkHreflangValues } = pageContext;

  const query = querystring.parse(search);
  const {
    SEOMetadata,
    store,
    description,
    productAttributes,
    videoGallery,
    relatedProducts,
    collection,
    warranty,
    manuals,
    supportPage,
    product,
    marketingContent,
  } = data.sanityGlobalProduct;

  const [activeSku, setActiveSku] = useState(null);
  const bvRef = useRef(null);

  const { tt, locale } = useSportdogInternationalContext();

  if (state?.searchedProduct) {
    product.searchedProduct = state.searchedProduct;
  }

  const productSchema = getProductSchema(product, null, activeSku);

  const breadcrumbs = [{ link: '/', text: tt(s => s.breadcrumbsCommon.home) }];

  if (collection) {
    breadcrumbs.push({
      link: `/${collection?.store?.slug?.current}`,
      text: collection?.store?.title,
    });
  }

  breadcrumbs.push({ text: product?.title });

  useEffect(() => {
    if (typeof window !== 'undefined' && !pathname.startsWith('/preview/')) {
      setActiveSku(query?.sku || '');
    }
  }, [pathname, query?.sku]);

  const [productSchemaData, setProductSchemaData] = useState(null);

  // Watch active sku parameter and update product schema data if sku changes
  useEffect(() => {
    setProductSchemaData(JSON.stringify(getProductSchema(product, activeSku)));
  }, [activeSku, product]);

  const selectedVariant =
    product.variants.find(variant => variant.sku === activeSku) ||
    product.variants[0];

  const productLifecycle = product?.productLifecycle?.value;
  const disableAddToCart = !!(
    productLifecycle === 'Obsolete' || productLifecycle === 'Coming Soon'
  );

  return (
    <Layout
      headTagData={{
        title: SEOMetadata?.title || product.title,
        description: SEOMetadata?.description || description,
        canonicalUrl: SEOMetadata?.canonicalUrl,
        noIndex: SEOMetadata?.noIndex,
        noFollow: SEOMetadata?.noFollow,
        schemaOrgObj: productSchema,
        meta: createOpenGraphTags(
          {
            title: SEOMetadata?.ogTitle || SEOMetadata?.title || product.title,
            description:
              SEOMetadata?.ogDescription ||
              SEOMetadata?.description ||
              description ||
              product.description,
            url: SEOMetadata?.ogUrl || SEOMetadata?.canonicalUrl || pathname,
            type: 'product',
            'og:price:currency': 'USD',
            'og:price:amount': selectedVariant?.price,
            image: SEOMetadata?.ogImage || store?.previewImageUrl,
          },
          'sportdog'
        ),
      }}
      scriptsData={[
        <script type="application/ld+json" key="pdp-schema">
          {productSchemaData}
        </script>,
      ]}
      linkHreflangValues={linkHreflangValues}
    >
      <BreadcrumbWithWrapper elements={breadcrumbs} />
      <ContentArea>
        <ProductContainer
          product={product}
          activeSku={activeSku}
          downloadsSection={{
            background: styles.colors.gray[800],
            textColor: styles.colors.white,
            supportLink: supportPage,
            heading: tt(s => s.productDetailPage.downloadSectionHeading),
          }}
          bvRatingColor="white"
          productData={{ supportManuals: manuals }}
          loadOldBV
          disableAddToCart={disableAddToCart}
          atGlance={data?.sanityGlobalProduct?.atGlance}
        >
          <Callouts>
            {warranty && warranty !== 'No Warranty' && (
              <PromoCallout
                Icon={IconWarranty}
                iconColor={styles.colors.white}
                topText={warranty}
                topTextColor={styles.colors.orange[500]}
                topTextTransform="uppercase"
                urlLink="/support/warranty"
              />
            )}
            <PromoCallout
              Icon={IconReturns}
              iconColor={styles.colors.white}
              topText={tt(
                s =>
                  s.productDetailPage.satisfactionGuaranteedPromoCalloutTopText
              )}
              topTextColor={styles.colors.orange[500]}
              topTextTransform="uppercase"
              bottomText={tt(
                s => s.productDetailPage.hassleFreeReturnsPromoCalloutBottomText
              )}
              bottomTextColor={styles.colors.white}
              urlLink="/support/warranty"
            />
            {!FREE_SHIPPING_DISABLED_FOR.includes(locale) && (
              <PromoCallout
                Icon={IconShipping}
                iconColor={styles.colors.white}
                topText={tt(
                  s => s.productDetailPage.freeShippingPromoCalloutTopText
                )}
                topTextColor={styles.colors.orange[500]}
                topTextTransform="uppercase"
                bottomText={tt(
                  s => s.productDetailPage.freeShippingConditionLabel
                )}
                bottomTextColor={styles.colors.white}
                urlLink="/support/policies-and-terms/shipping-policy"
              />
            )}
          </Callouts>
        </ProductContainer>
      </ContentArea>
      <PageWrapper padding="1rem">
        <LazyLoad offset={200}>
          {marketingContent?.blocks?.length > 0 && (
            <BlockBuilder
              blocks={marketingContent?.blocks}
              getInternalLink={getInternalLink}
              Button={Button}
            />
          )}
          {productAttributes && (
            <ProductAttributes productAttributes={productAttributes} />
          )}

          {videoGallery?.videos?.length > 0 && (
            <VideoGallery
              value={{
                headline: videoGallery?.headline,
                videos: videoGallery.videos,
              }}
            />
          )}

          {relatedProducts?.length > 0 && (
            <>
              <h2>{tt(s => s.productDetailPage.relatedProductsHeader)}</h2>
              <ProductCardGrid padding="0">
                {relatedProducts.map(relatedProduct => {
                  if (!relatedProduct?.store) {
                    return null;
                  }

                  const { store: relatedProductStore } = relatedProduct;

                  relatedProductStore.id = relatedProductStore?.shopifyId;
                  return (
                    <ProductCard
                      key={relatedProductStore.title}
                      product={{
                        ...relatedProduct,
                        ...relatedProductStore,
                        image: {
                          altText: relatedProductStore.title,
                          src: getResizedProductImage(
                            relatedProductStore?.previewImageUrl
                          ),
                        },
                        sku: relatedProductStore.variants[0]?.sku,
                        price: relatedProductStore.variants[0]?.price,
                        features: relatedProduct?.features,
                      }}
                      showInlineRatings={false}
                    />
                  );
                })}
              </ProductCardGrid>
            </>
          )}

          {showBazaarVoice(locale) && (
            <BazaarVoiceContainer ref={bvRef}>
              <h2>{tt(s => s.productDetailPage.reviewsHeader)}</h2>
              <Reviews sku={selectedVariant?.sku} />
              <h2>{tt(s => s.productDetailPage.questionsAndAnswersHeader)}</h2>
              <QandA sku={selectedVariant?.sku} />
            </BazaarVoiceContainer>
          )}
        </LazyLoad>
      </PageWrapper>
      <GearTagLine />
      {showBazaarVoice(locale) && (
        <Script
          defer
          key="bazaarvoice"
          src={`https://apps.bazaarvoice.com/deployments/sportdog/main_site/${process.env.GATSBY_BAZAARVOICE_ENVIRONMENT}/en_US/bv.js`}
          onLoad={() => {
            if (typeof window !== 'undefined') {
              window?.$BV?.configure('global', {
                productId: `${selectedVariant?.sku}`,
              });

              window?.$BV?.ui('rr', 'show_reviews', {
                doShowContent() {},
              });

              window?.$BV?.ui('qa', 'show_questions', {
                doShowContent() {},
              });
            }
          }}
        />
      )}
    </Layout>
  );
}

export const query = graphql`
  query ProductPageQuery($_id: String) {
    sanityGlobalProduct(_id: { eq: $_id }, siteIdentifier: { eq: "sportdog" }) {
      atGlance {
        keyItem
        valueItem
      }
      marketingContent: _rawLocalizedMarketingContent(
        resolveReferences: { maxDepth: 10 }
      )
      SEOMetadata {
        title
        description
        canonicalUrl
        noIndex
        noFollow
        ogTitle
        ogDescription
        ogUrl
        ogImage {
          asset {
            url
          }
        }
      }
      store {
        previewImageUrl
      }
      productAttributes: _rawProductAttributes(
        resolveReferences: { maxDepth: 3 }
      )
      videoGallery: _rawVideoGallery(resolveReferences: { maxDepth: 3 })
      discontinued
      supportPage
      relatedProducts: localizedRelatedProductsValue {
        features
        store {
          previewImageUrl
          shopifyId: gid
          title
          handle
          tags: tagsV2
          productType
          vendor
          priceRangeV2 {
            minVariantPrice {
              amount
            }
            maxVariantPrice {
              amount
            }
          }
          variants {
            shopifyId: gid
            availableForSale
            sku
            selectedOptions {
              name
              value
            }
            price
            compareAtPrice
          }
        }
      }
      manuals {
        asset: _rawAsset(resolveReferences: { maxDepth: 3 })
        label
      }
      collection {
        store {
          title
          slug {
            current
          }
        }
      }
      product: store {
        shopifyId: gid
        title
        handle
        productType
        description
        media {
          ... on SanityProductMediaImage {
            id
            image {
              altText
              src
              width
              height
            }
          }
          ... on SanityProductVideo {
            __typename
            id
            sources {
              url
              format
              width
              height
            }
            preview {
              image {
                src
                altText
                width
                height
              }
            }
          }
          ... on SanityProductExternalVideo {
            __typename
            id
            embeddedUrl
            preview {
              image {
                src
                altText
                width
                height
              }
            }
          }
        }
        productLifecycle
        vendor
        tags: tagsV2
        priceRangeV2 {
          maxVariantPrice {
            amount
            currencyCode
          }
          minVariantPrice {
            amount
            currencyCode
          }
        }
        options {
          name
          values
        }
        variants {
          title
          shopifyId: gid
          availableForSale
          sku
          selectedOptions {
            name
            value
          }
          price
          compareAtPrice
          media {
            ... on SanityProductMediaImage {
              id
              image {
                src
                altText
              }
            }
          }
          variantLifecycle
        }
      }
    }
  }
`;

ProductDetailTemplate.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
    href: PropTypes.string,
    pathname: PropTypes.string,
    state: PropTypes.shape({
      searchedProduct: PropTypes.string,
    }),
  }).isRequired,
  data: PropTypes.shape({
    sanityGlobalProduct: PropTypes.shape({
      store: PropTypes.shape({
        previewImageUrl: PropTypes.string,
      }),
      description: PropTypes.string,
      atGlance: PropTypes.arrayOf(PropTypes.shape({})),
      marketingContent: PropTypes.shape({
        blocks: PropTypes.arrayOf(PropTypes.shape({})),
      }),
      SEOMetadata: PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        canonicalUrl: PropTypes.string,
        noIndex: PropTypes.bool,
        noFollow: PropTypes.bool,
        ogTitle: PropTypes.string,
        ogDescription: PropTypes.string,
        ogUrl: PropTypes.string,
        ogImage: PropTypes.shape({
          asset: PropTypes.shape({
            url: PropTypes.string,
          }),
        }),
      }),
      productAttributes: PropTypes.shape({}),

      videoGallery: PropTypes.shape({
        headline: PropTypes.string,
        videos: PropTypes.arrayOf(PropTypes.shape({})),
      }),
      relatedProducts: PropTypes.arrayOf(PropTypes.shape({})),
      collection: PropTypes.shape({
        store: PropTypes.shape({
          title: PropTypes.string,
          slug: PropTypes.shape({
            current: PropTypes.string,
          }),
        }),
      }),
      warranty: PropTypes.string,
      manuals: PropTypes.arrayOf(PropTypes.shape({})),
      supportPage: PropTypes.string,
      product: PropTypes.shape({
        shopifyId: PropTypes.string,
        title: PropTypes.string,
        handle: PropTypes.string,
        productType: PropTypes.string,
        description: PropTypes.string,
        images: PropTypes.arrayOf(
          PropTypes.shape({
            src: PropTypes.string,
          })
        ),
        productLifecycle: PropTypes.shape({
          value: PropTypes.string,
        }),
        searchedProduct: PropTypes.string,
        tags: PropTypes.string,
        variants: PropTypes.arrayOf(
          PropTypes.shape({
            sku: PropTypes.string,
            price: PropTypes.number,
          })
        ),
      }),
    }),
  }).isRequired,
  pageContext: PropTypes.shape({
    linkHreflangValues: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
};

export default ProductDetailTemplate;
